import { toBrazilianDate } from '../../../../helpers/utils/utils';
import { hasUserPermissions } from '../../../../helpers/utils/permissoes';
import { formatBRL } from '../../UI';
import { statusAcordoColor } from '../../../theme/colors.constants';

/**
 * Status do acordo (`idStatusAcordo`, DTO real de `findAcordosService`) —
 * código numérico, NÃO uma string de enum. Mapeamento confirmado por
 * leitura direta de `Acordos.jsx` real (`makeConteudoTabela`): 0 Ativo, 1
 * Quebrado, 2 Quitado, 3 Simulação, 4 Proposta. Cores centralizadas em
 * `theme/colors.constants.statusAcordoColor` (não hex solto aqui) — outros
 * componentes futuros de Acordos (dialogs, chevron de parcelas) devem
 * reaproveitar a mesma constante em vez de duplicar os valores.
 */
export const STATUS_ACORDO = {
  0: { label: 'Ativo', cor: statusAcordoColor[0] },
  1: { label: 'Quebrado', cor: statusAcordoColor[1] },
  2: { label: 'Quitado', cor: statusAcordoColor[2] },
  3: { label: 'Simulação', cor: statusAcordoColor[3] },
  4: { label: 'Proposta', cor: statusAcordoColor[4] },
};

/**
 * Colunas da grid de Acordos (AW2-1133 Etapa 3) — ordem e `campo` exatos de
 * `AcordosFactory.makeCabecalhoTabelaAcordos` (produção): Número, Data,
 * Fase, Negociador, Status, Forma do Pagamento, Valor, Ações. `ordenarPor`
 * só nas 3 colunas com `OrdenacaoColuna` na tela real (Número, Data,
 * Negociador) — mesmo critério já usado em `titulosController.js`. Coluna
 * `acoes`: só placeholder visual nesta etapa (7 ícones desabilitados +
 * chevron), sem handlers reais ainda — ver etapas 5-11 do plano de
 * arquitetura.
 */
export const COLUNAS = [
  {
    campo: 'nroAcordo', label: 'Número', align: 'center', ordenarPor: 'nroAcordo',
  },
  {
    campo: 'dataAcordo', label: 'Data', align: 'center', ordenarPor: 'dataAcordo',
  },
  {
    campo: 'nomeFase', label: 'Fase', align: 'center', ordenarPor: null,
  },
  {
    campo: 'nomeUsuarioNegociador', label: 'Negociador', align: 'left', ordenarPor: 'nomeUsuarioNegociador',
  },
  {
    campo: 'idStatusAcordo', label: 'Status', align: 'center', ordenarPor: null,
  },
  {
    campo: 'descricaoFormaPagto', label: 'Forma de Pagamento', align: 'center', ordenarPor: null,
  },
  {
    campo: 'valor', label: 'Valor', align: 'right', ordenarPor: null,
  },
  {
    campo: 'acoes', label: 'Ações', align: 'center', ordenarPor: null,
  },
];

/**
 * Valor de exibição de uma célula do corpo, indexado pelo mesmo `campo` de
 * `COLUNAS` — mesmo padrão table-driven de `titulosController.formatarValorColuna`.
 * `dataAcordo` também passa por aqui (cai no fallback `acordo[campo]`, já
 * chega pré-formatada do mapper — só `valor` tem tratamento dedicado, por
 * ser o único campo monetário). Nunca chamada para `idStatusAcordo`/`acoes`
 * (renderizadas à parte por `TableAcordos.jsx` — a 1ª é um indicador visual,
 * não texto; a 2ª são botões interativos).
 */
export function formatarValorColuna(campo, acordo) {
  if (campo === 'valor') {
    return acordo.valor == null ? '—' : formatBRL(acordo.valor);
  }
  return acordo[campo];
}

/**
 * Forma de pagamento da parcela (`idTipoFormaPagto`) — código numérico,
 * mapeamento confirmado por leitura direta de `formaPgtoString` (`Acordos.jsx`
 * real): 0 Boleto, 1 Pix, 2 Cartão, 5 Dinheiro, 6 Cheque (3/4 não existem).
 */
export const FORMA_PAGAMENTO_PARCELA = {
  0: 'Boleto', 1: 'Pix', 2: 'Cartão', 5: 'Dinheiro', 6: 'Cheque',
};

/**
 * Status da parcela (`idStatusParcelaAcordo`) — código numérico, mapeamento
 * confirmado por leitura direta de `statusParcelaString` (`Acordos.jsx`
 * real): 0 Quitada, 1 Aguardando Envio, 2 Aguardando Pagamento, 3 Cancelado.
 */
export const STATUS_PARCELA_ACORDO = {
  0: 'Quitada', 1: 'Aguardando Envio', 2: 'Aguardando Pagamento', 3: 'Cancelado',
};

/**
 * Colunas do accordion de parcelas (AW2-1133 Etapa 4) — ordem e `campo`
 * exatos de `AcordosFactory.makeCabecalhoTabelaAcordosAccordion`
 * (produção): Nº Parcela, Forma do Pagamento, Vencimento, Valor, Status da
 * Parcela, Ações. Nenhuma coluna é ordenável na tela real (sem
 * `OrdenacaoColuna` no cabeçalho da accordion, diferente da grid principal).
 */
export const COLUNAS_PARCELAS = [
  { campo: 'nroParcela', label: 'Nº Parcela', align: 'center' },
  { campo: 'idTipoFormaPagto', label: 'Forma do Pagamento', align: 'center' },
  { campo: 'dataVencimento', label: 'Vencimento', align: 'center' },
  { campo: 'valor', label: 'Valor', align: 'right' },
  { campo: 'idStatusParcelaAcordo', label: 'Status da Parcela', align: 'center' },
  { campo: 'acoes', label: '', align: 'center' },
];

/**
 * Valor de exibição de uma célula do corpo do accordion, indexado pelo
 * mesmo `campo` de `COLUNAS_PARCELAS` — mesmo padrão table-driven de
 * `formatarValorColuna`. Nunca chamada para `acoes` (botões interativos,
 * renderizados à parte por `TableAcordoAccordion.jsx`).
 */
export function formatarValorColunaParcela(campo, parcela) {
  if (campo === 'valor') {
    return parcela.valor == null ? '—' : formatBRL(parcela.valor);
  }
  if (campo === 'idTipoFormaPagto') {
    return FORMA_PAGAMENTO_PARCELA[parcela.idTipoFormaPagto] ?? parcela.idTipoFormaPagto;
  }
  if (campo === 'idStatusParcelaAcordo') {
    return STATUS_PARCELA_ACORDO[parcela.idStatusParcelaAcordo] ?? parcela.idStatusParcelaAcordo;
  }
  if (campo === 'dataVencimento') {
    return parcela.dataVencimento ?? '—';
  }
  return parcela[campo];
}

/**
 * Mapeamento puro DTO real → shape de UI das parcelas de um acordo — mesmos
 * campos lidos por `makeConteudoTabelaAcordion` (`Acordos.jsx` real).
 * `dataVencimento` pré-formatada (`toBrazilianDate`) no mapper, mesmo
 * critério já usado para `dataAcordo` em `mapearAcordos` abaixo.
 */
function mapearParcelas(parcelas) {
  return (parcelas || []).map(parcela => ({
    id: parcela.idAcordoParcela,
    nroParcela: parcela.nroParcela,
    idTipoFormaPagto: parcela.idTipoFormaPagto,
    dataVencimento: parcela.dataVencimento ? toBrazilianDate(parcela.dataVencimento) : null,
    valor: parcela.valor,
    idStatusParcelaAcordo: parcela.idStatusParcelaAcordo,
    // Insumo do gate "Envelope" (AW2-1133 Etapa 8, ver `todasParcelasPagas`
    // abaixo) — já vem no mesmo payload de `findAcordosService`, sem fetch
    // adicional (mesmo padrão de `simulacaoExpirada` em `mapearAcordos`).
    valorPago: parcela.valorPago,
  }));
}

/**
 * Mapeamento puro DTO real → shape de UI — mesmos campos lidos por
 * `makeConteudoTabela` (`Acordos.jsx` real, confirmado por leitura direta do
 * código-fonte). `dataAcordo` pré-formatado (`toBrazilianDate`) no mapper,
 * mesmo padrão já usado para `vencimento` em `titulosController.mapearTitulos`.
 *
 * `dataValidade` mantido no shape sem consumidor nesta etapa (Etapa 3, só
 * grid) — é a data de expiração usada no tooltip de Simulação/Proposta na
 * tela real (`idStatusAcordo` 3/4); pré-preenche o indicador de status real
 * numa etapa futura (5-11) sem exigir alterar o mapper de novo, mesmo motivo
 * já documentado em `titulosController.mapearTitulos` para campos
 * auxiliares de dialog.
 *
 * `parcelas` (AW2-1133 Etapa 4) já vem ANINHADA no mesmo payload — nenhum
 * fetch novo, confirmado por leitura direta de
 * `configureStatesPesquisaAccordion` (`Acordos.jsx` real):
 * `data.content.map(item => item.parcelas)`.
 */
export function mapearAcordos(content) {
  return (content || []).map(acordo => ({
    id: acordo.idAcordo,
    nroAcordo: acordo.nroAcordo,
    dataAcordo: acordo.dataAcordo ? toBrazilianDate(acordo.dataAcordo) : null,
    nomeFase: acordo.nomeFase,
    nomeUsuarioNegociador: acordo.nomeUsuarioNegociador,
    idStatusAcordo: acordo.idStatusAcordo,
    dataValidade: acordo.dataValidade,
    descricaoFormaPagto: acordo.descricaoFormaPagto,
    valor: acordo.valor,
    // Texto do histórico do acordo (AW2-1133 Etapa 5) — já vem no mesmo
    // payload, consumido pelo dialog "Exibir Detalhes" (`DialogHistoricoAcordo.jsx`),
    // sem fetch adicional.
    texto: acordo.texto,
    // Insumos do gate "Mãozinha" (AW2-1133 Etapa 6, ver `isFecharAcordoDesabilitado`
    // abaixo) — já vêm no mesmo payload de `findAcordosService`, sem fetch
    // adicional (mesmo padrão de `texto` acima).
    simulacaoExpirada: acordo.simulacaoExpirada,
    simulacaoComTitulosLiberados: acordo.simulacaoComTitulosLiberados,
    parcelas: mapearParcelas(acordo.parcelas),
    // Insumo do gate "Relógio" (AW2-1133 Etapa 10, ver `isAnaliseDesabilitado`
    // abaixo) — já vem no mesmo payload de `findAcordosService`, sem fetch
    // adicional (mesmo padrão dos demais campos de gate acima).
    idUsuarioAutorizacao: acordo.idUsuarioAutorizacao,
  }));
}

/**
 * Gate de habilitação do botão "Mãozinha" (Fechar Acordo, AW2-1133 Etapa 6) —
 * confirmado por leitura direta de `Acordos.jsx` real (região do ícone
 * `FaHandshake`/`onClickGerarAcordo`, não `isDisabledButton` — esse outro
 * gate real controla o botão "Relógio"/Análise da Proposta, uma ação
 * distinta; o plano de arquitetura desta etapa referenciava o gate errado
 * por analogia, corrigido aqui após leitura direta do código-fonte real,
 * mesmo cuidado já documentado em `src/prototipo/CLAUDE.md` para suposições
 * não verificadas empiricamente).
 */
export function isFecharAcordoDesabilitado(acordo, podeModificarFase) {
  return !podeModificarFase
    || acordo?.simulacaoExpirada === true
    || acordo?.simulacaoComTitulosLiberados === false
    || acordo?.idStatusAcordo === 4;
}

/**
 * Título do botão "Mãozinha cortada" (AW2-1133 Etapa 7) — confirmado por
 * leitura direta de `getTituloBotaoCancelar` (`Acordos.jsx` real): rótulo
 * varia por `idStatusAcordo`, nunca fixo "Quebrar Acordo".
 */
export function getTituloBotaoCancelar(idStatusAcordo) {
  if (idStatusAcordo === 3) {
    return 'Cancelar Simulação';
  }
  if (idStatusAcordo === 4) {
    return 'Cancelar Proposta';
  }
  return 'Quebrar Acordo';
}

function isDataValidadeVigente(dataValidade) {
  return typeof dataValidade === 'string' && new Date().toISOString().slice(0, 10) <= dataValidade;
}

/**
 * Gate de habilitação do botão "Mãozinha cortada" (Quebrar/Cancelar Acordo,
 * AW2-1133 Etapa 7) — confirmado por leitura direta de `Acordos.jsx` real
 * (região do ícone `handShakeBlack`/`onClickButtonQuebrarAcordo`): habilitado
 * quando `idStatusAcordo === 0` (Ativo), OU quando é Simulação(3)/Proposta(4)
 * com `dataValidade` ainda vigente (`hoje <= dataValidade`, comparação de
 * string ISO, mesma forma da tela real); Quitado(2)/Quebrado(1), ou
 * Simulação/Proposta expiradas, ficam sempre desabilitados.
 */
export function isQuebrarAcordoDesabilitado(acordo, podeModificarFase) {
  if (!podeModificarFase) {
    return true;
  }
  if (acordo?.idStatusAcordo === 0) {
    return false;
  }
  const simulacaoVigente = acordo?.idStatusAcordo === 3 && isDataValidadeVigente(acordo?.dataValidade);
  const propostaVigente = acordo?.idStatusAcordo === 4 && isDataValidadeVigente(acordo?.dataValidade);
  return !simulacaoVigente && !propostaVigente;
}

/**
 * `todasParcelasPagas` (AW2-1133 Etapa 8) — porta byte-a-byte de
 * `Acordos.jsx` real: array vazio retorna `true` (sem parcela para cobrar,
 * mesmo comportamento do `forEach` real que nunca executa), qualquer parcela
 * com `valorPago` ausente/zerado faz retornar `false`.
 */
function todasParcelasPagas(parcelas) {
  return (parcelas || []).every(parcela => Boolean(parcela.valorPago));
}

/**
 * Gate de habilitação do botão "Envelope" (Enviar Termo por E-mail, AW2-1133
 * Etapa 8) — confirmado por leitura direta de `Acordos.jsx` real (região do
 * ícone `FaEnvelope`/`setOpenDialogEnviarEmail`): desabilitado sem
 * `podeModificarFase`, com Simulação expirada, para qualquer status
 * diferente de Ativo(0), ou quando todas as parcelas já foram pagas (nada a
 * cobrar por e-mail).
 */
export function isEnviarEmailDesabilitado(acordo, podeModificarFase) {
  return !podeModificarFase
    || acordo?.simulacaoExpirada === true
    || acordo?.idStatusAcordo !== 0
    || todasParcelasPagas(acordo?.parcelas);
}

/**
 * Gate de habilitação do botão "Imprimir" (AW2-1133 Etapa 9) — confirmado
 * por leitura direta de `Acordos.jsx` real: usa exatamente a MESMA condição
 * do ícone "Envelope" (mesmo bloco JSX duplicado byte-a-byte na tela real,
 * não uma coincidência a desfazer aqui) — alias explícito em vez de
 * duplicar a fórmula, para as 2 ações continuarem em sincronia se a regra
 * mudar num futuro achado de código-fonte real.
 */
export const isImprimirDesabilitado = isEnviarEmailDesabilitado;

/**
 * Gate de habilitação do botão "Relógio" (Análise da Proposta/Simulação,
 * AW2-1133 Etapa 10) — confirmado por leitura direta de `Acordos.jsx` real
 * (`isDisabledButton`): esse gate NÃO depende de `podeModificarFase` (achado
 * já registrado na Etapa 6 deste módulo — `isDisabledButton` controla
 * "Relógio", não "Mãozinha"). Habilitado só quando: `idStatusAcordo === 4`
 * (Proposta) E `simulacaoComTitulosLiberados !== false` E `texto` não
 * começa com "Proposta Não Aprovada " E o processo NÃO está na fase "Acordo
 * Seguro Externo" E o usuário logado tem a permissão 1206 E é o mesmo
 * `idUsuarioAutorizacao` do acordo E a proposta ainda está vigente
 * (`hoje <= dataValidade`, reaproveita `isDataValidadeVigente` da Etapa 7).
 *
 * `hasUserPermissions` lê o Redux store imperativamente (`store.getState()`)
 * — chamado direto aqui, sem `useMemo`/deps `[]`, para não repetir o mesmo
 * risco de closure obsoleta já documentado em `helpers/CLAUDE.md` para esse
 * helper (a tela real memoiza com `[]`, um pré-existente não replicado de
 * propósito).
 */
export function isAnaliseDesabilitado(acordo, podeModificarFase, extra) {
  const { idUsuarioLogado, ehFaseAcordoSeguro } = extra || {};
  if (acordo?.idStatusAcordo !== 4
    || acordo?.simulacaoComTitulosLiberados === false
    || acordo?.texto?.substring(0, 22) === 'Proposta Não Aprovada '
    || ehFaseAcordoSeguro) {
    return true;
  }
  if (!hasUserPermissions(1206)) {
    return true;
  }
  return !(isDataValidadeVigente(acordo?.dataValidade) && acordo?.idUsuarioAutorizacao === idUsuarioLogado);
}

/**
 * Gate de habilitação do botão "Indicadores" (Distribuição Personalizada,
 * AW2-1133 Etapa 11) — confirmado por leitura direta de `Acordos.jsx` real
 * (região do ícone `PollIcon`): habilitado só quando `idStatusAcordo === 0`
 * (Ativo) E o usuário logado tem a permissão 1903 E o processo NÃO está na
 * fase "Acordo Seguro Externo". Diferente de `isFecharAcordoDesabilitado`/
 * `isQuebrarAcordoDesabilitado` (Etapas 6/7), este gate real NÃO depende de
 * `podeModificarFase` — confirmado que o 2º parâmetro não é lido em nenhum
 * ponto da condição real, mesmo achado já registrado para `isAnaliseDesabilitado`
 * (Etapa 10). `hasUserPermissions` chamado direto (sem memoização), mesmo
 * critério já usado em `isAnaliseDesabilitado` para não repetir o risco de
 * closure obsoleta documentado em `helpers/CLAUDE.md`.
 */
export function isDistribuicaoPersonalizadaDesabilitado(acordo, podeModificarFase, extra) {
  const { ehFaseAcordoSeguro } = extra || {};
  return acordo?.idStatusAcordo !== 0 || !hasUserPermissions(1903) || Boolean(ehFaseAcordoSeguro);
}
